import { Calendar } from "@/components/ui/calendar";
import { Card } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { EditContentDialog } from "@/components/EditContentDialog";

interface ScheduledPost {
  id: string;
  content: string;
  scheduled_for: string;
  timezone: string;
}

const CalendarPage = () => {
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [scheduledPosts, setScheduledPosts] = useState<ScheduledPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingPost, setEditingPost] = useState<{ id: string; content: string; scheduled_for: string } | null>(null);

  useEffect(() => {
    fetchScheduledPosts();
  }, []);

  const fetchScheduledPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('scheduled_posts')
        .select('*')
        .order('scheduled_for', { ascending: true });

      if (error) throw error;
      setScheduledPosts(data || []);
    } catch (error) {
      console.error('Error fetching scheduled posts:', error);
      toast.error('Failed to load scheduled posts');
    } finally {
      setIsLoading(false);
    }
  };

  const getDayPosts = (day: Date) => {
    return scheduledPosts.filter(post => 
      new Date(post.scheduled_for).toDateString() === day.toDateString()
    );
  };

  const handlePostUpdated = () => {
    fetchScheduledPosts();
    setEditingPost(null);
  };

  return (
    <div className="container h-[calc(100vh-4rem)] py-4 flex flex-col">
      <h1 className="text-2xl font-bold mb-4">Calendar</h1>
      <Card className="flex-1 p-4 overflow-auto">
        <Calendar
          mode="single"
          selected={date}
          onSelect={setDate}
          className="rounded-lg w-full h-full"
          components={{
            DayContent: ({ date: dayDate }) => {
              const posts = getDayPosts(dayDate);
              return (
                <div className="w-[140px] h-[140px] p-2 overflow-hidden">
                  <div className="text-sm mb-2">{dayDate.getDate()}</div>
                  <div className="flex flex-col gap-1.5 overflow-hidden">
                    {posts.map((post) => (
                      <Badge 
                        key={post.id}
                        variant="outline"
                        className="text-xs py-0.5 px-2 truncate cursor-pointer hover:bg-accent"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingPost({ 
                            id: post.id, 
                            content: post.content,
                            scheduled_for: post.scheduled_for
                          });
                        }}
                      >
                        {format(new Date(post.scheduled_for), 'HH:mm')} - {post.content.substring(0, 20)}
                      </Badge>
                    ))}
                  </div>
                </div>
              );
            },
          }}
        />
        
        {date && getDayPosts(date).length > 0 && (
          <div className="mt-6 border-t pt-4">
            <h3 className="font-medium mb-3">Scheduled Posts for {format(date, 'MMMM d, yyyy')}</h3>
            <div className="space-y-3">
              {getDayPosts(date).map(post => (
                <div 
                  key={post.id} 
                  className="p-3 bg-muted rounded-lg cursor-pointer hover:bg-accent/50"
                  onClick={() => setEditingPost({ 
                    id: post.id, 
                    content: post.content,
                    scheduled_for: post.scheduled_for
                  })}
                >
                  <div className="flex items-center justify-between mb-1">
                    <Badge variant="outline">
                      {format(new Date(post.scheduled_for), 'HH:mm')}
                    </Badge>
                  </div>
                  <p className="text-sm">{post.content}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <EditContentDialog
        open={!!editingPost}
        onOpenChange={(open) => !open && setEditingPost(null)}
        initialContent={editingPost?.content || ""}
        contentType="scheduled"
        itemId={editingPost?.id || ""}
        onContentUpdated={handlePostUpdated}
        scheduledFor={editingPost?.scheduled_for}
      />
    </div>
  );
};

export default CalendarPage;